// src/components/Author/AuthorProfileModal.jsx
import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { User, Globe, Building2, X, ChevronDown, ChevronUp } from 'lucide-react';
import AboutBookModal from '../AboutBookModal';
import axios from 'axios';
import { BACKEND_URL } from '@/config/api';

const AuthorProfileModal = ({ isOpen, onClose, author }) => {
  const [showLongBio, setShowLongBio] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [showBookModal, setShowBookModal] = useState(false);
  const [isReadingAloud, setIsReadingAloud] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const audioRef = React.useRef(null);

  // Stop audio when modal closes
  React.useEffect(() => {
    if (!isOpen && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsReadingAloud(false);
    }
  }, [isOpen]);

  // Cleanup audio on unmount
  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  if (!author) return null;

  const displayName = author.pen_name || `${author.first_name} ${author.last_name}`;
  const hasLongBio = author.long_bio && author.long_bio !== author.short_bio;

  const handleReadAloud = async () => {
    if (isReadingAloud && audioRef.current) {
      // Stop current playback
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsReadingAloud(false);
      return;
    }

    try {
      setTtsLoading(true);
      // BACKEND_URL imported from centralized config
      
      // Fetch TTS audio
      const response = await fetch(`${BACKEND_URL}/api/public/author-profiles/${author.author_id}/tts`);
      
      if (!response.ok) {
        throw new Error('Failed to generate audio');
      }
      
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Play audio
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play();
        setIsReadingAloud(true);
        
        // Handle audio end
        audioRef.current.onended = () => {
          setIsReadingAloud(false);
          URL.revokeObjectURL(audioUrl);
        };
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      alert('Failed to play audio. Please try again.');
    } finally {
      setTtsLoading(false);
    }
  };

  const handleBookClick = async (bookData) => {
    // Fetch full book details to show in AboutBookModal
    try {
      const API = BACKEND_URL;
      const response = await axios.get(`${API}/api/manuscripts/${bookData.manuscript_id}`);
      setSelectedBook(response.data);
      setShowBookModal(true);
    } catch (error) {
      console.error('Error fetching book details:', error);
      // Fallback: create a basic book object from available data
      setSelectedBook({
        id: bookData.manuscript_id,
        title: bookData.title,
        author: bookData.author,
        cover_image_path: bookData.cover_image_url,
        purchase_url: bookData.purchase_url
      });
      setShowBookModal(true);
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 text-slate-50">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-slate-400 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start gap-4 mb-4">
            {/* Headshot */}
            {author.headshot_image_url ? (
              <img
                src={`${BACKEND_URL}${author.headshot_image_url}`}
                alt={displayName}
                className="h-20 w-20 rounded-full object-cover border-4 border-purple-500/30 shadow-lg"
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-slate-800 border-4 border-purple-500/30 flex items-center justify-center">
                <User className="h-10 w-10 text-slate-500" />
              </div>
            )}

            {/* Name & Tagline */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2">{displayName}</h2>
              {author.tagline && (
                <p className="text-base text-purple-300 italic">"{author.tagline}"</p>
              )}
            </div>
          </div>

          {/* Contact & Publisher Info */}
          <div className="flex flex-wrap gap-4 text-sm text-slate-300">
            {author.website_url && (
              <a
                href={author.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-blue-400 transition-colors"
              >
                <Globe className="h-4 w-4" />
                Website
              </a>
            )}
            {author.publisher_name && (
              <div className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                {author.publisher_url ? (
                  <a
                    href={author.publisher_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition-colors"
                  >
                    {author.publisher_name}
                  </a>
                ) : (
                  <span>{author.publisher_name}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bio Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white">About {author.first_name || displayName}</h3>
            <button
              onClick={handleReadAloud}
              disabled={ttsLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors shadow-lg"
            >
              {ttsLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Loading...</span>
                </>
              ) : isReadingAloud ? (
                <>
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>Stop</span>
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                  </svg>
                  <span>Read Aloud</span>
                </>
              )}
            </button>
          </div>
          
          {/* Hidden audio element */}
          <audio ref={audioRef} style={{ display: 'none' }} />
          
          {/* Short Bio */}
          {author.short_bio && (
            <div className="text-slate-300 leading-relaxed mb-3 whitespace-pre-wrap">
              {author.short_bio}
            </div>
          )}

          {/* Long Bio (expandable) */}
          {hasLongBio && (
            <div>
              {showLongBio ? (
                <>
                  <div className="text-slate-300 leading-relaxed mb-3 whitespace-pre-wrap">
                    {author.long_bio}
                  </div>
                  <button
                    onClick={() => setShowLongBio(false)}
                    className="text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
                  >
                    <ChevronUp className="h-4 w-4" />
                    Show less
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowLongBio(true)}
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
                >
                  <ChevronDown className="h-4 w-4" />
                  Read more about this author
                </button>
              )}
            </div>
          )}
        </div>

        {/* Titles List */}
        {author.titles && author.titles.length > 0 && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white mb-3">
              Other titles by this author on NewAudia
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {author.titles.map((book) => (
                <button
                  key={book.manuscript_id}
                  onClick={() => handleBookClick(book)}
                  className="text-left bg-slate-800/50 hover:bg-slate-800 border border-slate-700 rounded-lg p-2 transition-colors group"
                >
                  {book.cover_image_url ? (
                    <img
                      src={`${BACKEND_URL}${book.cover_image_url}`}
                      alt={book.title}
                      className="w-full aspect-[2/3] object-cover rounded mb-2"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`w-full aspect-[2/3] bg-slate-700 rounded mb-2 items-center justify-center ${book.cover_image_url ? 'hidden' : 'flex'}`}>
                    <span className="text-slate-500 text-xs text-center px-2">No cover</span>
                  </div>
                  <p className="text-xs text-slate-200 group-hover:text-white font-medium line-clamp-2">
                    {book.title}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-slate-700">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>

      {/* About Book Modal for clicked books */}
      {selectedBook && (
        <AboutBookModal
          book={selectedBook}
          isOpen={showBookModal}
          onClose={() => {
            setShowBookModal(false);
            setSelectedBook(null);
          }}
          onAddToLibrary={async (bookId) => {
            // Close all modals first
            setShowBookModal(false);
            setSelectedBook(null);
            onClose(); // Close author profile modal
            
            // Add to library
            try {
              const token = localStorage.getItem('accessToken');
              // BACKEND_URL imported from centralized config
              const response = await fetch(`${BACKEND_URL}/api/reader/library`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ manuscript_id: bookId })
              });
              
              if (response.ok) {
                console.log('Book added to library');
              }
            } catch (error) {
              console.error('Error adding to library:', error);
            }
          }}
          onReadNow={async (bookId) => {
            // Close all modals first
            setShowBookModal(false);
            setSelectedBook(null);
            onClose(); // Close author profile modal
            
            // Navigate to reader
            try {
              const token = localStorage.getItem('accessToken');
              // BACKEND_URL imported from centralized config
              
              // Add to library first
              await fetch(`${BACKEND_URL}/api/reader/library`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ manuscript_id: bookId })
              });
              
              // Create session
              const sessionResponse = await fetch(`${BACKEND_URL}/api/manuscripts/${bookId}/open`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (sessionResponse.ok) {
                const sessionData = await sessionResponse.json();
                // Navigate using window.location to ensure all modals close
                window.location.href = `/player/${sessionData.session_id}`;
              }
            } catch (error) {
              console.error('Error starting reading:', error);
            }
          }}
          isAuthenticated={true}
        />
      )}
    </>
  );
};

export default AuthorProfileModal;
